import { ButtonBase } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import { useAgent } from "../agent/AgentProvider";

const c = brainTokens.color;

/**
 * The `✦ Explain` ghost pill. Always-visible variant (per the design's pulse
 * cards). Seeds the agent widget in explain mode with a snapshot + prompt,
 * always requesting the smart model. Phase 5 adds the hover-reveal variant.
 */
export function ExplainButton({
  snapshot,
  prompt,
  label = "Explain",
  sx,
}: {
  snapshot: Record<string, unknown>;
  prompt?: string;
  label?: string;
  sx?: SxProps<Theme>;
}) {
  const { openAgent, pageContext } = useAgent();

  const defaultPrompt = () => {
    const where = pageContext?.entity ? ` for ${pageContext.entity.name ?? pageContext.entity.id}` : "";
    const tab = pageContext?.tab ? ` (${pageContext.tab} tab)` : "";
    const unit = snapshot.unit ? ` ${String(snapshot.unit)}` : "";
    return `Explain this: ${String(snapshot.label)} = ${String(snapshot.value)}${unit}${where}${tab}. Use tools to find out why it is what it is; cite every claim.`;
  };

  return (
    <ButtonBase
      onClick={() => openAgent({ kind: "explain", snapshot, prompt: prompt ?? defaultPrompt(), modelHint: "smart" })}
      aria-label={`Explain ${String(snapshot.label ?? label)}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: 10.5,
        fontWeight: 500,
        fontFamily: brainTokens.font.sans,
        color: c.explainText,
        background: c.explainBg,
        border: `1px solid ${c.explainBorder}`,
        borderRadius: `${brainTokens.radius.pill}px`,
        padding: "3px 9px",
        "&:hover": { background: "#d9efec", color: c.teal },
        ...sx,
      }}
    >
      ✦ {label}
    </ButtonBase>
  );
}
